/**
 * @file DecorPositionField.tsx
 * @description Место рисунка на схеме секции (phase19.16, партия SB120,
 * этап 1; uiux-sb120-stage1 §2.7; данные db-design-sb120-stage1 §1
 * `look.decor[].x/y` — % от левого верхнего угла рамки):
 *
 * ```
 * ┌──────────────────────┐   тап — точка сюда; стрелки — по 5 %
 * │            ●         │   x [ 60 ] %   y [ 20 ] %
 * └──────────────────────┘   [↖][↑][↗][←][·][→][↙][↓][↘] — 9 мест
 * ```
 *
 * Сетка 3×3 у рисунков больше не используется: 9 мест — кнопки-подсказки
 * (x, y ∈ {0, 50, 100}, то же место, что прежние точки).
 *
 * SB120 этап 2 (uiux-sb120-stage2 §2 «Фон и рамка»): то же поле у рисунков
 * фона сайта и страницы — схема окна (`schemeClassName`) с пояснением
 * области (`hint`: окно без 96 px сверху и 48 снизу).
 */

import type { KeyboardEvent, MouseEvent } from "react";

import { Label } from "../../../ui/label";
import { FieldHint } from "../../../ui/settings-groups";
import { NumberSliderField } from "../../../site-fields/NumberSliderField";
import { ANCHOR_NAMES } from "../../../../lib/site-builder/section-meta";
import { cn } from "@/lib/utils";

interface Point {
  x: number;
  y: number;
}

interface Props {
  label: string;
  value: Point;
  onChange: (point: Point) => void;
  /** Живой предпросмотр одной координаты. */
  onPreview?: (axis: "x" | "y", value: number | undefined) => void;
  /** Пропорции схемы вместо секции 16:7 (окно у рисунков фона). */
  schemeClassName?: string;
  /** Пояснение под схемой: что за область. */
  hint?: string;
}

const clamp = (v: number) => Math.round(Math.min(100, Math.max(0, v)));
const STEPS = [0, 50, 100] as const;

export function DecorPositionField({ label, value, onChange, onPreview, schemeClassName, hint }: Props) {
  function onClick(event: MouseEvent<HTMLButtonElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    onChange({ x: clamp(((event.clientX - rect.left) / rect.width) * 100), y: clamp(((event.clientY - rect.top) / rect.height) * 100) });
  }
  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    const d: Record<string, [number, number]> = { ArrowLeft: [-5, 0], ArrowRight: [5, 0], ArrowUp: [0, -5], ArrowDown: [0, 5] };
    const step = d[event.key];
    if (!step) return;
    event.preventDefault();
    onChange({ x: clamp(value.x + step[0]), y: clamp(value.y + step[1]) });
  }
  const axis = (key: "x" | "y", text: string) => (
    <NumberSliderField
      label={text}
      value={value[key]}
      inherit={{ value: 50, label: "по центру" }}
      min={0}
      max={100}
      step={1}
      unit="%"
      onChange={(v) => onChange({ ...value, [key]: v ?? 50 })}
      onPreview={onPreview ? (v) => onPreview(key, v ?? 50) : undefined}
    />
  );

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <Label>{label}</Label>
      <button
        type="button"
        onClick={onClick}
        onKeyDown={onKeyDown}
        aria-label={`${label}: ${value.x} % слева, ${value.y} % сверху. Нажмите на схему, чтобы переставить, или двигайте стрелками`}
        className={cn(
          "relative aspect-[16/7] w-full max-w-80 rounded-xl bg-surface-low outline-none [box-shadow:inset_0_0_0_1px_var(--outline-variant)] focus-visible:ring-2 focus-visible:ring-ring",
          schemeClassName,
        )}
      >
        <span aria-hidden className="pointer-events-none absolute size-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-primary shadow" style={{ left: `${value.x}%`, top: `${value.y}%` }} />
      </button>
      {hint ? <FieldHint className="mt-0">{hint}</FieldHint> : null}
      <div role="group" aria-label={`${label}: девять мест`} className="grid w-max grid-cols-3 gap-1">
        {ANCHOR_NAMES.map((name, i) => {
          const point = { x: STEPS[i % 3] ?? 50, y: STEPS[Math.floor(i / 3)] ?? 50 };
          const active = point.x === value.x && point.y === value.y;
          return (
            <button
              key={name}
              type="button"
              aria-pressed={active}
              aria-label={name}
              title={name}
              onClick={() => onChange(point)}
              className={cn(
                "relative size-12 rounded-lg bg-surface-low outline-none focus-visible:ring-2 focus-visible:ring-ring can-hover:size-9",
                active && "[box-shadow:inset_0_0_0_2px_var(--primary)]",
              )}
            >
              <span aria-hidden className="absolute size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-on-surface-variant" style={{ left: `${15 + point.x * 0.7}%`, top: `${15 + point.y * 0.7}%` }} />
            </button>
          );
        })}
      </div>
      {axis("x", "Слева, %")}
      {axis("y", "Сверху, %")}
    </div>
  );
}
